import { useMemo, useState } from "react";
import { useNavigate } from "react-router";
import { Plus, PenLine, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  useListCarrierAddOnServices,
  useCreateCarrierAddOnService,
  type CreateCarrierAddOnServiceInput,
} from "@/api/carrier-add-on-services";
import { useGetCarriers, type Carrier } from "@/api/carriers";
import type {
  CarrierAddOnService,
  SignatureServiceDetails,
} from "@/api.types.v2";
import {
  useCarrierScope,
  useCenterScopeNotApplicable,
} from "@/contexts/carrier-scope-context";

// Resolve a providerCarrierId to a display name, falling back to the raw id.
function carrierName(carriers: Carrier[] | undefined, id: string): string {
  return carriers?.find((c) => c.id === id)?.name ?? id;
}

// cents → "$12.34" (rounded to 2dp). Returns em-dash on null.
function formatCents(cents: number | null | undefined): string {
  if (cents == null) return "—";
  return `$${(cents / 100).toFixed(2)}`;
}

// Render the SIGNATURE pricing column. Falls back to "Not configured" when no
// flat fee is set.
function renderPricing(d: SignatureServiceDetails | null | undefined) {
  if (d?.flatFeeCents == null) {
    return <span className="text-muted-foreground italic">Not configured</span>;
  }
  return (
    <span className="text-sm">Flat fee · {formatCents(d.flatFeeCents)}</span>
  );
}

export function SignatureServicesScreen() {
  // Add-on services are provider-carrier-scoped (providerCarrierId), not
  // center-scoped. Disable the center picker.
  useCenterScopeNotApplicable();
  // Add-on services endpoint takes a single `providerCarrierId`. Use the
  // first selected carrier as a narrowing hint; the in-screen tabs group
  // by carrier.
  const { carrierIds: scopedCarrierIds } = useCarrierScope();
  const scopedCarrierId = scopedCarrierIds[0];
  const {
    data: services,
    isLoading,
    isError,
    refetch,
  } = useListCarrierAddOnServices({
    type: "SIGNATURE",
    providerCarrierId: scopedCarrierId,
  });
  const { data: carriersResp, isLoading: carriersLoading } = useGetCarriers();
  const carriers = carriersResp?.data;

  const [createOpen, setCreateOpen] = useState(false);

  // Group services by provider carrier — mirrors PackagingServicesScreen.
  const groups = useMemo(() => {
    const byCarrier = new Map<string, CarrierAddOnService[]>();
    for (const svc of services ?? []) {
      const list = byCarrier.get(svc.providerCarrierId) ?? [];
      list.push(svc);
      byCarrier.set(svc.providerCarrierId, list);
    }
    return [...byCarrier.entries()]
      .map(([carrierId, svcs]) => ({
        carrierId,
        name: carrierName(carriers, carrierId),
        services: svcs,
      }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [services, carriers]);

  const [activeTab, setActiveTab] = useState<string | undefined>(undefined);
  const currentTab = activeTab ?? groups[0]?.carrierId;
  const loading = isLoading || carriersLoading;

  return (
    <div className="p-4 space-y-4">
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-3xl font-bold">Signature Services</h1>
          <p className="text-gray-600 mt-2 max-w-2xl">
            Signature service tiers (standard signature, adult signature, etc.),
            grouped by the provider carrier that operates them. Codes are
            lowercase snake_case and permanent (unique per carrier) — retire a
            service by toggling it inactive.
          </p>
        </div>
        <Button
          onClick={() => setCreateOpen(true)}
          disabled={!carriers || carriers.length === 0}
        >
          <Plus className="h-4 w-4 mr-1" />
          Add signature service
        </Button>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>
            {isError
              ? "Signature services"
              : loading
                ? "Loading…"
                : `${services?.length ?? 0} service${
                    services?.length === 1 ? "" : "s"
                  } across ${groups.length} carrier${
                    groups.length === 1 ? "" : "s"
                  }`}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {isError ? (
            <div className="text-center p-8 text-muted-foreground">
              <AlertTriangle className="mx-auto h-12 w-12 mb-2 text-destructive opacity-70" />
              <p>Couldn&apos;t load signature services.</p>
              <Button
                variant="outline"
                size="sm"
                className="mt-3"
                onClick={() => void refetch()}
              >
                Try again
              </Button>
            </div>
          ) : loading ? (
            <div className="space-y-2">
              {[1, 2, 3].map((i) => (
                <Skeleton key={i} className="h-12 w-full" />
              ))}
            </div>
          ) : groups.length === 0 ? (
            <div className="text-center p-8 text-muted-foreground">
              <PenLine className="mx-auto h-12 w-12 mb-2 opacity-50" />
              <p>No signature services yet.</p>
              <p className="text-xs mt-1">
                Add one to a provider carrier to make it available in pricing.
              </p>
            </div>
          ) : (
            <Tabs value={currentTab} onValueChange={setActiveTab}>
              <TabsList className="flex-wrap h-auto">
                {groups.map((g) => (
                  <TabsTrigger key={g.carrierId} value={g.carrierId}>
                    {g.name}
                    <Badge
                      variant="secondary"
                      className="ml-2 bg-zinc-100 text-zinc-700"
                    >
                      {g.services.length}
                    </Badge>
                  </TabsTrigger>
                ))}
              </TabsList>
              {groups.map((g) => (
                <TabsContent key={g.carrierId} value={g.carrierId}>
                  <CarrierServiceTable services={g.services} />
                </TabsContent>
              ))}
            </Tabs>
          )}
        </CardContent>
      </Card>

      <CreateSignatureServiceDialog
        open={createOpen}
        onOpenChange={setCreateOpen}
        carriers={carriers ?? []}
        defaultCarrierId={currentTab}
      />
    </div>
  );
}

function CarrierServiceTable({
  services,
}: {
  services: CarrierAddOnService[];
}) {
  const navigate = useNavigate();

  if (services.length === 0) {
    return (
      <div className="text-center p-8 text-muted-foreground">
        <PenLine className="mx-auto h-10 w-10 mb-2 opacity-50" />
        <p>No signature services for this carrier yet.</p>
        <p className="text-xs mt-1">
          Use “Add signature service” to create one for this carrier.
        </p>
      </div>
    );
  }

  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>Code</TableHead>
          <TableHead>Name</TableHead>
          <TableHead>Pricing</TableHead>
          <TableHead>Status</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {services.map((svc) => {
          const href = `/add-on-services/signature/${svc.id}`;
          return (
            <TableRow
              key={svc.id}
              role="button"
              tabIndex={0}
              aria-label={`Open ${svc.name}`}
              className="hover:bg-muted/50 cursor-pointer focus-visible:outline-hidden focus-visible:ring-1 focus-visible:ring-ring"
              onClick={() => navigate(href)}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  void navigate(href);
                }
              }}
            >
              <TableCell className="font-mono text-sm">{svc.code}</TableCell>
              <TableCell>{svc.name}</TableCell>
              <TableCell>{renderPricing(svc.signatureDetails)}</TableCell>
              <TableCell>
                {svc.active ? (
                  <Badge
                    variant="secondary"
                    className="bg-emerald-100 text-emerald-800 hover:bg-emerald-100"
                  >
                    Active
                  </Badge>
                ) : (
                  <Badge
                    variant="secondary"
                    className="bg-zinc-100 text-zinc-700 hover:bg-zinc-100"
                  >
                    Inactive
                  </Badge>
                )}
              </TableCell>
            </TableRow>
          );
        })}
      </TableBody>
    </Table>
  );
}

// Create dialog — header fields + optional flat-fee pricing. Admin can also
// fill the flat fee on the detail screen later.
function CreateSignatureServiceDialog({
  open,
  onOpenChange,
  carriers,
  defaultCarrierId,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  carriers: Carrier[];
  defaultCarrierId?: string;
}) {
  const create = useCreateCarrierAddOnService();
  const [providerCarrierId, setProviderCarrierId] = useState("");
  const [code, setCode] = useState("");
  const [name, setName] = useState("");
  const [flatFeeDollars, setFlatFeeDollars] = useState("");

  const reset = () => {
    setProviderCarrierId("");
    setCode("");
    setName("");
    setFlatFeeDollars("");
  };

  const handleClose = (next: boolean) => {
    if (!next) reset();
    onOpenChange(next);
  };

  const handleSubmit = () => {
    const carrierId = providerCarrierId || defaultCarrierId || "";
    if (!carrierId) {
      toast.error("Pick a provider carrier.");
      return;
    }

    const payload: CreateCarrierAddOnServiceInput = {
      providerCarrierId: carrierId,
      type: "SIGNATURE",
      code: code.trim().toLowerCase(),
      name: name.trim(),
    };

    if (flatFeeDollars.trim()) {
      const n = Number(flatFeeDollars);
      if (!Number.isFinite(n) || n < 0) {
        toast.error("Flat fee must be a non-negative number.");
        return;
      }
      payload.signature = { flatFeeCents: Math.round(n * 100) };
    }

    if (!payload.code || !payload.name) {
      toast.error("Code and name are required.");
      return;
    }
    if (!/^[a-z0-9_]+$/.test(payload.code)) {
      toast.error(
        "Code must be lowercase letters, digits, and underscores only."
      );
      return;
    }

    create.mutate(payload, {
      onSuccess: () => {
        toast.success(`Signature service "${payload.name}" created.`);
        reset();
        onOpenChange(false);
      },
      onError: (err: unknown) => {
        const e = err as {
          response?: { status?: number; data?: { error?: string } };
        };
        if (e.response?.status === 409) {
          toast.error(
            "A signature service with this code already exists for this carrier."
          );
        } else {
          toast.error(e.response?.data?.error ?? "Failed to create service.");
        }
      },
    });
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>New signature service</DialogTitle>
          <DialogDescription>
            Pick the provider carrier that operates this service. Code is
            permanent and unique per carrier — pick something stable like{" "}
            <code>standard</code> or <code>adult</code>. Lowercase snake_case
            only. Flat fee is optional — leave blank and configure later from
            the detail screen.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <div className="space-y-1">
            <Label htmlFor="sg-carrier">Provider carrier</Label>
            {/* Native <select>: Radix Select inside a Radix Dialog recurses
                under jsdom — match the packaging-create-dialog pattern. */}
            <select
              id="sg-carrier"
              value={providerCarrierId}
              onChange={(e) => setProviderCarrierId(e.target.value)}
              className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-xs focus-visible:outline-hidden focus-visible:ring-1 focus-visible:ring-ring"
            >
              <option value="" disabled>
                Select a carrier
              </option>
              {carriers.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </select>
          </div>
          <div className="space-y-1">
            <Label htmlFor="sg-code">Code</Label>
            <Input
              id="sg-code"
              value={code}
              onChange={(e) =>
                setCode(
                  e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, "_")
                )
              }
              placeholder="standard"
              className="font-mono"
            />
            <p className="text-xs text-muted-foreground">
              Lowercase letters, digits, and underscores only.
            </p>
          </div>
          <div className="space-y-1">
            <Label htmlFor="sg-name">Name</Label>
            <Input
              id="sg-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Standard Signature"
            />
          </div>
          <div className="space-y-1">
            <Label htmlFor="sg-flat">Flat fee (CAD)</Label>
            <Input
              id="sg-flat"
              type="number"
              step="any"
              value={flatFeeDollars}
              onChange={(e) => setFlatFeeDollars(e.target.value)}
              placeholder="5"
            />
            <p className="text-xs text-muted-foreground">
              Fixed fee charged when this signature is requested. Leave blank
              and set later.
            </p>
          </div>
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => handleClose(false)}
            disabled={create.isPending}
          >
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={create.isPending}>
            {create.isPending ? "Creating…" : "Create"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
